import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AIFeedbackItem,
  AIFeedbackOrder,
  AIFeedbackSortBy,
  AIFeedbackUser,
  getAIFeedbackAdminList,
  getOrganizations,
  Organization,
  resolveAIFeedback,
  unresolveAIFeedback,
} from '../../../utils/adminApiUtils';
import { useI18n } from '../../../contexts/I18nContext';
import CustomSelect from '../../../components/CustomSelect';

function formatFeedbackUser(user: AIFeedbackItem['user']): string {
  if (user == null) return '—';
  if (typeof user === 'string') return user;
  const u = user as AIFeedbackUser;
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email || ''})` : (u.email || '—');
}

function formatAiResponseForDisplay(raw: string): { formatted: string; isJson: boolean } {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { formatted: '', isJson: false };
  try {
    const parsed = JSON.parse(trimmed);
    return { formatted: JSON.stringify(parsed, null, 2), isJson: true };
  } catch {
    return { formatted: trimmed, isJson: false };
  }
}

const AIFeedbackManager: React.FC = (): React.ReactElement => {
  const { t } = useI18n();

  const [items, setItems] = useState<AIFeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);
  const [sortBy, setSortBy] = useState<AIFeedbackSortBy>('time');
  const [order, setOrder] = useState<AIFeedbackOrder>('desc');
  const [organizationId, setOrganizationId] = useState<string>('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [aiResponseDialogText, setAiResponseDialogText] = useState<string | null>(null);

  const loadOrganizations = useCallback(async () => {
    try {
      const response = await getOrganizations({ limit: 500, offset: 0, sort_by: 'name', sort_order: 'asc' });
      setOrganizations(response.organizations || []);
    } catch {
      setOrganizations([]);
    }
  }, []);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAIFeedbackAdminList({
        skip,
        limit,
        sort_by: sortBy,
        order,
        organization_id: organizationId ? Number(organizationId) : undefined,
      });
      setItems(response.items || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.feedback.error.load'));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, sortBy, order, organizationId, t]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  useEffect(() => {
    setSkip(0);
  }, [limit, sortBy, order, organizationId]);

  const handleStatusToggle = useCallback(
    async (item: AIFeedbackItem) => {
      try {
        setUpdatingId(item.id);
        const updated =
          item.status === 'resolved'
            ? await unresolveAIFeedback(item.id)
            : await resolveAIFeedback(item.id);

        setItems((prev) => prev.map((x) => (x.id === item.id ? updated : x)));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('admin.feedback.error.status'));
      } finally {
        setUpdatingId(null);
      }
    },
    [t]
  );

  const pageRange = useMemo(() => {
    if (!total || items.length === 0) return '0-0';
    return `${skip + 1}-${Math.min(skip + items.length, total)}`;
  }, [skip, items.length, total]);

  return (
    <div className="users-section">
      <div className="section-header">
        <h2>{t('admin.feedback.title')}</h2>
      </div>

      <div className="users-controls">
        <div className="search-filters">
          <div className="filters-desktop">
            <CustomSelect
              value={sortBy}
              onChange={(value) => setSortBy(value.toString() as AIFeedbackSortBy)}
              className=""
              placeholder={t('admin.feedback.sortBy')}
              options={[
                { value: 'time', label: t('admin.feedback.sort.time') },
                { value: 'status', label: t('admin.feedback.sort.status') },
              ]}
            />

            <CustomSelect
              value={order}
              onChange={(value) => setOrder(value.toString() as AIFeedbackOrder)}
              className=""
              placeholder={t('admin.feedback.order')}
              options={[
                { value: 'desc', label: t('admin.feedback.order.desc') },
                { value: 'asc', label: t('admin.feedback.order.asc') },
              ]}
            />

            <CustomSelect
              value={organizationId}
              onChange={(value) => setOrganizationId(value.toString())}
              className=""
              placeholder={t('admin.feedback.organization')}
              options={[
                { value: '', label: t('admin.feedback.organization.all') },
                ...organizations.map((org) => ({ value: org.id, label: org.name })),
              ]}
            />

            <CustomSelect
              value={limit}
              onChange={(value) => setLimit(Number(value))}
              className=""
              placeholder={t('admin.feedback.limit')}
              options={[
                { value: 20, label: '20' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={fetchFeedback}>{t('admin.tryAgain')}</button>
        </div>
      )}

      {loading ? (
        <div className="loading">{t('admin.loading')}</div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="admin-table users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('admin.feedback.table.user')}</th>
                  <th>{t('admin.feedback.table.organization')}</th>
                  <th>{t('admin.feedback.table.specialty')}</th>
                  <th>{t('admin.feedback.table.messageId')}</th>
                  <th>{t('admin.feedback.table.chatId')}</th>
                  <th>{t('admin.feedback.table.aiText')}</th>
                  <th>{t('admin.feedback.table.comment')}</th>
                  <th>{t('admin.feedback.table.status')}</th>
                  <th>{t('admin.feedback.table.createdAt')}</th>
                  <th>{t('admin.feedback.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{formatFeedbackUser(item.user)}</td>
                    <td>{item.organization_name || '—'}</td>
                    <td>{item.specialty_name || '—'}</td>
                    <td>{item.message_id ?? '—'}</td>
                    <td>{item.chat_id ?? '—'}</td>
                    <td
                      className="feedback-ai-text-cell feedback-ai-text-cell-clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => setAiResponseDialogText(item.ai_response_text || '')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setAiResponseDialogText(item.ai_response_text || '');
                        }
                      }}
                      title={t('admin.feedback.viewFull')}
                    >
                      {item.ai_response_text || '—'}
                    </td>
                    <td className="feedback-comment-cell">{item.user_comment || '—'}</td>
                    <td>
                      <span className={`status-dot ${item.status === 'resolved' ? 'active' : 'inactive'}`}>
                        {item.status === 'resolved' ? t('admin.feedback.status.resolved') : t('admin.feedback.status.unresolved')}
                      </span>
                    </td>
                    <td>{new Date(item.created_at).toLocaleString('ru-RU')}</td>
                    <td>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => handleStatusToggle(item)}
                        disabled={updatingId === item.id}
                      >
                        {item.status === 'resolved'
                          ? t('admin.feedback.action.unresolve')
                          : t('admin.feedback.action.resolve')}
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={11}>
                      <div className="empty-state">
                        <p>{t('admin.feedback.empty')}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination pt-3">
            <button
              onClick={() => setSkip(Math.max(0, skip - limit))}
              disabled={skip === 0}
              className="pagination-btn"
            >
              {t('admin.pagination.prev')}
            </button>
            <span className="pagination-info">
              {t('admin.pagination.showingPrefix')} {pageRange} {t('admin.pagination.showingSuffix')} {total}
            </span>
            <button
              onClick={() => setSkip(skip + limit)}
              disabled={skip + items.length >= total}
              className="pagination-btn"
            >
              {t('admin.pagination.next')}
            </button>
          </div>

          {aiResponseDialogText !== null && (
            <div
              className="dialog-overlay feedback-view-overlay"
              onClick={() => setAiResponseDialogText(null)}
              role="dialog"
              aria-modal="true"
              aria-label={t('admin.feedback.viewFull')}
            >
              <div
                className="feedback-view-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="feedback-view-dialog-header">
                  <h3>{t('admin.feedback.aiResponseFull')}</h3>
                  <button
                    type="button"
                    className="dialog-close-button"
                    onClick={() => setAiResponseDialogText(null)}
                    aria-label={t('dialogs.close')}
                  >
                    ×
                  </button>
                </div>
                <div className="feedback-view-dialog-body">
                  {(() => {
                    const { formatted, isJson } = formatAiResponseForDisplay(aiResponseDialogText);
                    return (
                      <pre className={isJson ? 'feedback-view-json' : 'feedback-view-plain'}>
                        {formatted || '—'}
                      </pre>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AIFeedbackManager;
